import { ApiError } from "../api/client";
import type { Locale } from "../stores/locale";
import { t, type MessageKey } from "./i18n";

// toUserMessage turns any caught value into a friendly, localized, single-line
// message fit for a toast. It is the one place that decides "what does the user
// read when an operation fails" — so a raw Go/git error string (e.g. "exit
// status 128", "pq: duplicate key") never reaches the UI.
//
// The raw error is the caller's job to keep for debugging: every catch that
// uses this should also `console.error(err)`. toUserMessage only produces the
// human-facing line.
//
// Mapping order (most specific first):
//   1. ApiError.code  — structured BE signals (needs_seat, github_token_expired…)
//   2. ApiError.status — HTTP class (401/403/404/409/422/5xx…)
//   3. message text    — network/offline + git failure patterns from the worker
//   4. fallback        — generic "something went wrong"
//
// A caller may pass `fallbackKey` to override the generic tail with an
// action-specific message (e.g. errors.action.save) when no structured cause
// matched — so "couldn't save" reads better than "something went wrong".

// Structured BE codes we have a dedicated phrasing for. Anything not listed
// falls through to the status / message / generic ladder.
const CODE_KEYS: Record<string, MessageKey> = {
  needs_seat: "errors.code.needsSeat",
  github_token_expired: "errors.code.githubTokenExpired",
  rate_limited: "errors.http.429",
  // Re-snapshot / certificate mint over a galaxy with no observed self orbit (409).
  // The explicit phrasing replaces the generic "couldn't seal" so the user knows
  // it's an empty-observation state, not a failure to fix by retrying.
  no_observed_orbit: "errors.code.noObservedOrbit",
  // Repo toggle hit a free-plan quota (402/403). Both are upgrade-resolvable, so
  // say WHY the toggle failed (cap reached / too large) + point at Nova, instead
  // of the generic "update failed" that hid an actionable, non-retryable cause.
  repo_limit_reached: "errors.code.repoLimitReached",
  repo_too_large_for_plan: "errors.code.repoTooLargeForPlan",
  // Manual re-observation hit the per-day cap (402). Every observe button (Logbook,
  // Observatory, …) gets the real cause here instead of a generic "couldn't observe."
  observation_quota_exceeded: "errors.code.observationQuotaExceeded",
};

function statusKey(status: number): MessageKey | null {
  if (status === 401) return "errors.http.401";
  if (status === 403) return "errors.http.403";
  if (status === 404) return "errors.http.404";
  if (status === 409) return "errors.http.409";
  if (status === 422) return "errors.http.422";
  if (status === 429) return "errors.http.429";
  if (status >= 500) return "errors.http.5xx";
  return null;
}

// messageKey catches causes that aren't carried as a status: a thrown
// TypeError from fetch() when the network is down/offline, and raw git/worker
// failure strings that occasionally surface in a thrown message.
function messageKey(raw: string): MessageKey | null {
  const m = raw.toLowerCase();
  if (/failed to fetch|networkerror|network error|load failed|err_internet|offline/.test(m))
    return "errors.network";
  if (/not found|404|does not exist/.test(m)) return "errors.git.notFound";
  if (/permission|denied|authentication failed|could not read username|terminal prompts disabled/.test(m))
    return "errors.git.permission";
  if (/timeout|timed out|deadline exceeded/.test(m)) return "errors.git.timeout";
  if (/clone|exit status|fatal|git/.test(m)) return "errors.git.clone";
  return null;
}

export function errorMessageKey(err: unknown, fallbackKey?: MessageKey): MessageKey {
  if (err instanceof ApiError) {
    if (err.code && CODE_KEYS[err.code]) return CODE_KEYS[err.code];
    const sk = statusKey(err.status);
    if (sk) return sk;
  }
  // Offline check works for any error: if the browser knows it's offline, that's
  // the real cause regardless of what the thrown message says.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "errors.network";

  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (message) {
    const mk = messageKey(message);
    if (mk) return mk;
  }
  return fallbackKey ?? "errors.generic";
}

export function toUserMessage(err: unknown, locale: Locale, fallbackKey?: MessageKey): string {
  return t(errorMessageKey(err, fallbackKey), locale);
}
